import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import HelpButton from "../components/HelpButton";
import {
  ChartLineUp, Users, Eye, ChartPieSlice, FileText, ChartBar,
  TrendUp, Lightning, Receipt, Trophy, ArrowRight, FunnelSimple,
  Buildings, Megaphone,
} from "@phosphor-icons/react";

const REPORT_CARDS = [
  {
    section: "Phase 6 — New",
    items: [
      {
        title: "Pipeline Conversion Velocity",
        desc: "Avg days at each stage (Lead → Deal → Quote → SO). Identify slowdowns.",
        icon: TrendUp, color: "from-indigo-500 to-blue-600",
        path: "/reports/pipeline-velocity",
        testid: "card-pipeline-velocity",
      },
      {
        title: "BDE Workload Distribution",
        desc: "Active leads/deals/quotes per BDE. Auto-flag overload (>2× team avg).",
        icon: Users, color: "from-emerald-500 to-teal-600",
        path: "/reports/bde-workload",
        testid: "card-bde-workload",
      },
      {
        title: "Public-PDF Engagement vs Conversion",
        desc: "Cross-reference PDF views with quote→SO conversion. Spot ghosted prospects.",
        icon: Eye, color: "from-fuchsia-500 to-purple-600",
        path: "/reports/public-pdf-engagement",
        testid: "card-public-pdf-engagement",
      },
    ],
  },
  {
    section: "Billing & Sales",
    items: [
      { title: "Outstanding Aging", desc: "Customer-wise overdue buckets.", icon: Receipt, color: "from-amber-500 to-orange-600", path: "/billing", testid: "card-aging" },
      { title: "Win/Loss", desc: "Quote conversion analytics.", icon: Trophy, color: "from-rose-500 to-pink-600", path: "/billing", testid: "card-win-loss" },
      { title: "Quotation Aging", desc: "Time spent in approval stages.", icon: FunnelSimple, color: "from-sky-500 to-cyan-600", path: "/billing", testid: "card-quote-aging" },
      { title: "Discount Deviation", desc: "Outliers vs policy thresholds.", icon: ChartLineUp, color: "from-violet-500 to-indigo-600", path: "/billing", testid: "card-discount" },
    ],
  },
  {
    section: "Pipeline & Performance",
    items: [
      { title: "Sales Forecasting", desc: "Weighted pipeline + monthly projections.", icon: ChartLineUp, color: "from-blue-500 to-indigo-600", path: "/dashboard", testid: "card-forecast" },
      { title: "Sales Funnel", desc: "Stage-to-stage conversion rates.", icon: FunnelSimple, color: "from-teal-500 to-emerald-600", path: "/dashboard", testid: "card-funnel" },
      { title: "Team Leaderboard", desc: "Revenue, deals, activities per agent.", icon: Trophy, color: "from-yellow-500 to-amber-600", path: "/dashboard", testid: "card-leaderboard" },
      { title: "Lead Dashboard", desc: "14-day trends + top performers.", icon: ChartBar, color: "from-lime-500 to-green-600", path: "/leads-dashboard", testid: "card-leads-dash" },
    ],
  },
  {
    section: "Marketing & Campaigns",
    items: [
      { title: "Marketing Campaign ROI", desc: "Per-campaign spend vs revenue.", icon: Megaphone, color: "from-pink-500 to-rose-600", path: "/marketing-campaigns", testid: "card-mktg-roi" },
      { title: "Campaign Lead Performance", desc: "MQL/SQL/Won by campaign.", icon: Lightning, color: "from-orange-500 to-red-600", path: "/marketing-campaigns", testid: "card-mktg-lead" },
    ],
  },
  {
    section: "Operations",
    items: [
      { title: "Admin Billing Dashboard", desc: "Users, GST gaps, audit exceptions.", icon: Buildings, color: "from-slate-500 to-zinc-600", path: "/my-dashboard", testid: "card-admin-billing" },
      { title: "Approvals Center", desc: "Pending billing + marketing approvals.", icon: FileText, color: "from-cyan-500 to-blue-600", path: "/approvals", testid: "card-approvals" },
      { title: "CSV / PDF Exports", desc: "Download products, leads, contacts, deals, campaigns.", icon: ChartBar, color: "from-stone-500 to-neutral-700", path: "/reports", testid: "card-csv-exports" },
    ],
  },
];

export default function ReportsHub({ user, logout }) {
  const navigate = useNavigate();
  const allowedRoles = ["admin", "manager", "cfo", "marketing_head", "bdm"];
  const canViewNewReports = allowedRoles.includes(user?.role);

  if (user?.role === "customer") {
    return (
      <div className="flex min-h-screen bg-slate-50">
        <Sidebar user={user} logout={logout} />
        <div className="flex-1 ml-64 p-12 text-slate-500" data-testid="reports-hub-customer-blocked">
          Reports are not available for customer accounts.
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      <div className="flex-1 ml-64">
        <div className="page-header-sticky">
          <div className="px-8 pt-5 pb-5 max-w-7xl">
            <div className="flex items-center gap-3 mb-1">
              <div className="text-[11px] tracking-[0.18em] uppercase text-slate-500 font-semibold">Phase 6 · Workspace</div>
              <HelpButton module="reports" />
            </div>
            <h1 className="text-[42px] leading-tight font-black text-slate-900 tracking-tight" data-testid="reports-hub-title">
              Reports Hub
            </h1>
            <p className="text-slate-600 mt-1 text-base">
              All your sales, billing, marketing & operational reports — one place.
            </p>
          </div>
        </div>

        <div className="px-8 pt-6 pb-12 max-w-7xl space-y-10">
          {REPORT_CARDS.map((sec, sIdx) => {
            const isNewSection = sec.section.startsWith("Phase 6");
            if (isNewSection && !canViewNewReports) return null;
            return (
              <div key={sec.section} data-testid={`reports-section-${sIdx}`}>
                <div className="flex items-baseline gap-3 mb-4">
                  <h2 className="text-lg font-bold text-slate-900">{sec.section}</h2>
                  {isNewSection && (
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full font-bold">
                      NEW
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {sec.items.map(card => {
                    const Icon = card.icon;
                    return (
                      <button
                        key={card.title}
                        onClick={() => navigate(card.path)}
                        data-testid={card.testid}
                        className="group text-left bg-white border border-slate-200 hover:border-slate-300 hover:shadow-lg transition-all p-5 rounded-2xl flex flex-col gap-3"
                      >
                        <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${card.color} flex items-center justify-center shadow-sm`}>
                          <Icon size={22} weight="bold" className="text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="font-bold text-slate-900 mb-1 leading-snug">{card.title}</div>
                          <div className="text-sm text-slate-500 leading-snug">{card.desc}</div>
                        </div>
                        <div className="flex items-center text-xs font-semibold text-slate-400 group-hover:text-slate-700 transition-colors">
                          Open report <ArrowRight size={12} weight="bold" className="ml-1" />
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
